import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ShopLayout from '../components/shop/ShopLayout';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { formatPrice } from '../utils/shopHelpers';
import './CheckoutPage.css';
import './auth/AuthForm.css';
import Seo from '../components/seo/Seo';

const STEPS = ['Shipping', 'Billing', 'Delivery', 'Payment', 'Review'];

const emptyAddress = {
  first_name: '', last_name: '', company: '', address_line1: '', address_line2: '',
  city: '', state: '', postal_code: '', country: 'Kenya', phone: ''
};

function CheckoutPage() {
  const navigate = useNavigate();
  const { items, subtotal, clearCart } = useCart();
  const { customer, isAuthenticated } = useAuth();
  const [step, setStep] = useState(0);
  const [shippingAddress, setShippingAddress] = useState(emptyAddress);
  const [billingAddress, setBillingAddress] = useState(emptyAddress);
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [shippingMethod, setShippingMethod] = useState('standard');
  const [shippingMethods, setShippingMethods] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('manual');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (customer) {
      setShippingAddress((a) => ({
        ...a,
        first_name: customer.first_name,
        last_name: customer.last_name,
        phone: customer.phone || ''
      }));
      setGuestEmail(customer.email);
      setGuestFirstName(customer.first_name);
      setGuestLastName(customer.last_name);
      setGuestPhone(customer.phone || '');
    }
  }, [customer]);

  useEffect(() => {
    (async () => {
      try {
        const [shipRes, payRes] = await Promise.all([
          api.getShippingMethods(),
          api.getPaymentMethods()
        ]);
        setShippingMethods(Array.isArray(shipRes?.data) ? shipRes.data : []);
        setPaymentMethods(Array.isArray(payRes?.data) ? payRes.data : []);
      } catch {
        setShippingMethods([
          { id: 'standard', name: 'Standard Delivery', cost: 500, days: '3-5 business days' },
          { id: 'express', name: 'Express Delivery', cost: 1500, days: '1-2 business days' },
          { id: 'pickup', name: 'Warehouse Pickup', cost: 0, days: 'Same day' }
        ]);
        setPaymentMethods([
          { id: 'manual', name: 'Manual Invoice', description: 'Pay by invoice' },
          { id: 'bank_transfer', name: 'Bank Transfer', description: 'Transfer to our account' },
          { id: 'mpesa', name: 'M-Pesa', description: 'Mobile money payment' },
          { id: 'card', name: 'Credit / Debit Card', description: 'Card payment' }
        ]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const res = await api.getCustomerAddresses();
        const list = Array.isArray(res?.data) ? res.data : [];
        setSavedAddresses(list);
        const def = list.find((a) => a.is_default) || list[0];
        if (def) {
          setShippingAddress({
            first_name: def.first_name,
            last_name: def.last_name,
            company: def.company || '',
            address_line1: def.address_line1,
            address_line2: def.address_line2 || '',
            city: def.city,
            state: def.state || '',
            postal_code: def.postal_code || '',
            country: def.country || 'Kenya',
            phone: def.phone || ''
          });
        }
      } catch {
        // ignore
      }
    })();
  }, [isAuthenticated]);

  const shippingCost = useMemo(() => {
    const m = shippingMethods.find((s) => s.id === shippingMethod);
    return m?.cost ?? 500;
  }, [shippingMethods, shippingMethod]);

  const tax = useMemo(() => Math.round(subtotal * 0.16 * 100) / 100, [subtotal]);
  const total = useMemo(
    () => Math.round((subtotal + shippingCost + tax) * 100) / 100,
    [subtotal, shippingCost, tax]
  );

  const AddressForm = ({ address, setAddress, prefix }) => (
    <div className="auth-form">
      {!isAuthenticated && prefix === 'ship' && (
        <>
          <div className="auth-form__row">
            <div className="auth-form__field"><label>First Name *</label><input required value={guestFirstName} onChange={(e) => setGuestFirstName(e.target.value)} /></div>
            <div className="auth-form__field"><label>Last Name *</label><input required value={guestLastName} onChange={(e) => setGuestLastName(e.target.value)} /></div>
          </div>
          <div className="auth-form__row">
            <div className="auth-form__field"><label>Email *</label><input type="email" required value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} /></div>
            <div className="auth-form__field"><label>Phone</label><input value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} /></div>
          </div>
        </>
      )}
      <div className="auth-form__row">
        <div className="auth-form__field"><label>First Name</label><input required value={address.first_name} onChange={(e) => setAddress({ ...address, first_name: e.target.value })} /></div>
        <div className="auth-form__field"><label>Last Name</label><input required value={address.last_name} onChange={(e) => setAddress({ ...address, last_name: e.target.value })} /></div>
      </div>
      <div className="auth-form__field"><label>Company</label><input value={address.company} onChange={(e) => setAddress({ ...address, company: e.target.value })} /></div>
      <div className="auth-form__field"><label>Address Line 1 *</label><input required value={address.address_line1} onChange={(e) => setAddress({ ...address, address_line1: e.target.value })} /></div>
      <div className="auth-form__field"><label>Address Line 2</label><input value={address.address_line2} onChange={(e) => setAddress({ ...address, address_line2: e.target.value })} /></div>
      <div className="auth-form__row">
        <div className="auth-form__field"><label>City *</label><input required value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} /></div>
        <div className="auth-form__field"><label>State/County</label><input value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} /></div>
      </div>
      <div className="auth-form__row">
        <div className="auth-form__field"><label>Postal Code</label><input value={address.postal_code} onChange={(e) => setAddress({ ...address, postal_code: e.target.value })} /></div>
        <div className="auth-form__field"><label>Country</label><input value={address.country} onChange={(e) => setAddress({ ...address, country: e.target.value })} /></div>
      </div>
      <div className="auth-form__field"><label>Phone</label><input value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} /></div>
    </div>
  );

  const validateShipping = () => {
    if (!shippingAddress.address_line1 || !shippingAddress.city) {
      setError('Please complete the shipping address');
      return false;
    }
    if (!isAuthenticated && (!guestEmail || !guestFirstName || !guestLastName)) {
      setError('Guest checkout requires name and email');
      return false;
    }
    return true;
  };

  const placeOrder = async () => {
    setError('');
    setPlacing(true);
    try {
      const billing = sameAsBilling ? shippingAddress : billingAddress;
      const payload = {
        items: items.map((i) => ({ product_id: i.productId, quantity: i.quantity })),
        shipping_address: shippingAddress,
        billing_address: billing,
        shipping_method: shippingMethod,
        payment_method: paymentMethod,
        payment_data: paymentMethod === 'mpesa' ? { phone: mpesaPhone } : {},
        notes,
        ...(isAuthenticated ? {} : {
          guest_email: guestEmail,
          guest_first_name: guestFirstName,
          guest_last_name: guestLastName,
          guest_phone: guestPhone || shippingAddress.phone
        })
      };
      const res = await api.createOrder(payload);
      clearCart();
      navigate(`/order-success/${res.data.order.order_number}`, {
        state: { order: res.data.order, payment: res.data.payment }
      });
    } catch (err) {
      setError(err.message || 'Could not place order');
    } finally {
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <ShopLayout breadcrumbs={[{ label: 'Checkout' }]}>
        <Seo title="Checkout" path="/checkout" />
        <div className="container checkout-page">
          <div className="checkout-panel" style={{ textAlign: 'center' }}>
            <p>Your cart is empty.</p>
            <Link to="/shop">Continue Shopping</Link>
          </div>
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout breadcrumbs={[{ label: 'Checkout' }]}>
      <Seo title="Checkout" path="/checkout" />
      <div className="container checkout-page">
        <div className="checkout-steps">
          {STEPS.map((label, i) => (
            <div key={label} className={`checkout-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
              {i + 1}. {label}
            </div>
          ))}
        </div>

        <div className="checkout-layout">
          <div className="checkout-panel">
            {error && <div className="auth-form__error" style={{ marginBottom: 16 }}>{error}</div>}

            {step === 0 && (
              <>
                <h2>Shipping Address</h2>
                {!isAuthenticated && (
                  <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
                    Checking out as guest. <Link to="/login" state={{ from: { pathname: '/checkout' } }}>Sign in</Link> to use saved addresses.
                  </p>
                )}
                {savedAddresses.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <label>Saved address: </label>
                    <select onChange={(e) => {
                      const a = savedAddresses.find((x) => String(x.id) === e.target.value);
                      if (a) setShippingAddress({
                        first_name: a.first_name, last_name: a.last_name, company: a.company || '',
                        address_line1: a.address_line1, address_line2: a.address_line2 || '',
                        city: a.city, state: a.state || '', postal_code: a.postal_code || '',
                        country: a.country || 'Kenya', phone: a.phone || ''
                      });
                    }}>
                      <option value="">Select…</option>
                      {savedAddresses.map((a) => (
                        <option key={a.id} value={a.id}>{a.address_line1}, {a.city}</option>
                      ))}
                    </select>
                  </div>
                )}
                <AddressForm address={shippingAddress} setAddress={setShippingAddress} prefix="ship" />
              </>
            )}

            {step === 1 && (
              <>
                <h2>Billing Address</h2>
                <label className="auth-form__checkbox">
                  <input type="checkbox" checked={sameAsBilling} onChange={(e) => setSameAsBilling(e.target.checked)} />
                  Same as shipping address
                </label>
                {!sameAsBilling && (
                  <AddressForm address={billingAddress} setAddress={setBillingAddress} prefix="bill" />
                )}
              </>
            )}

            {step === 2 && (
              <>
                <h2>Shipping Method</h2>
                {shippingMethods.map((m) => (
                  <label key={m.id} className={`shipping-option ${shippingMethod === m.id ? 'selected' : ''}`}>
                    <input type="radio" name="shipping" checked={shippingMethod === m.id} onChange={() => setShippingMethod(m.id)} />
                    <div>
                      <strong>{m.name}</strong> — {formatPrice(m.cost)}<br />
                      <span style={{ fontSize: 13, color: '#6b7280' }}>{m.days}</span>
                    </div>
                  </label>
                ))}
              </>
            )}

            {step === 3 && (
              <>
                <h2>Payment Method</h2>
                {paymentMethods.map((m) => (
                  <label key={m.id} className={`payment-option ${paymentMethod === m.id ? 'selected' : ''}`}>
                    <input type="radio" name="payment" checked={paymentMethod === m.id} onChange={() => setPaymentMethod(m.id)} />
                    <div>
                      <strong>{m.name}</strong>
                      <span>{m.description}</span>
                    </div>
                  </label>
                ))}
                {paymentMethod === 'mpesa' && (
                  <div className="auth-form__field" style={{ marginTop: 12 }}>
                    <label>M-Pesa Phone Number</label>
                    <input type="tel" placeholder="2547XXXXXXXX" value={mpesaPhone} onChange={(e) => setMpesaPhone(e.target.value)} />
                  </div>
                )}
              </>
            )}

            {step === 4 && (
              <>
                <h2>Review Order</h2>
                <div className="review-block">
                  <h4>Shipping to</h4>
                  <p>{shippingAddress.first_name} {shippingAddress.last_name}<br />
                  {shippingAddress.address_line1}, {shippingAddress.city}, {shippingAddress.country}</p>
                </div>
                <div className="review-block">
                  <h4>Delivery</h4>
                  <p>{shippingMethods.find((m) => m.id === shippingMethod)?.name}</p>
                </div>
                <div className="review-block">
                  <h4>Payment</h4>
                  <p>{paymentMethods.find((m) => m.id === paymentMethod)?.name}</p>
                </div>
                <div className="review-block">
                  <h4>Items ({items.length})</h4>
                  {items.map((i) => (
                    <p key={i.productId}>{i.name} × {i.quantity} — {formatPrice(i.price * i.quantity)}</p>
                  ))}
                </div>
                <div className="auth-form__field">
                  <label>Order Notes (optional)</label>
                  <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Special instructions…" />
                </div>
              </>
            )}

            <div className="checkout-nav">
              {step > 0 ? (
                <button type="button" className="checkout-nav__back" onClick={() => { setError(''); setStep(step - 1); }}>
                  ← Back
                </button>
              ) : <Link to="/cart" className="checkout-nav__back" style={{ padding: '12px 24px', textDecoration: 'none' }}>← Cart</Link>}
              {step < STEPS.length - 1 ? (
                <button type="button" className="checkout-nav__next" onClick={() => {
                  setError('');
                  if (step === 0 && !validateShipping()) return;
                  setStep(step + 1);
                }}>Continue →</button>
              ) : (
                <button type="button" className="checkout-nav__next" disabled={placing} onClick={placeOrder}>
                  {placing ? 'Placing Order…' : 'Place Order'}
                </button>
              )}
            </div>
          </div>

          <aside className="checkout-summary">
            <h3>Order Summary</h3>
            {items.map((i) => (
              <div key={i.productId} className="checkout-summary__row">
                <span>{i.name} × {i.quantity}</span>
                <span>{formatPrice(i.price * i.quantity)}</span>
              </div>
            ))}
            <div className="checkout-summary__row"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
            <div className="checkout-summary__row"><span>Shipping</span><span>{formatPrice(shippingCost)}</span></div>
            <div className="checkout-summary__row"><span>Tax (16%)</span><span>{formatPrice(tax)}</span></div>
            <div className="checkout-summary__row checkout-summary__total"><span>Total</span><span>{formatPrice(total)}</span></div>
          </aside>
        </div>
      </div>
    </ShopLayout>
  );
}

export default CheckoutPage;
